import { gql, TypedDocumentNode } from '@common/hooks';
import {
  ScheduleReviewQueryCgResponse,
  ScheduleReviewQueryVariables,
} from '@common/graphql';

// Raw query-response shapes for one placed shift, one demand cell and one
// team-member row, mapped to the FE domain shapes (ScheduleShiftType /
// ScheduleDemandType / ScheduleTeamMemberType) by the page service.
export type ScheduleReviewShiftType = NonNullable<
  ScheduleReviewQueryCgResponse['scheduleReview']
>['weeks'][number]['shifts'][number];

export type ScheduleReviewDemandType = NonNullable<
  ScheduleReviewQueryCgResponse['scheduleReview']
>['weeks'][number]['demands'][number];

export type ScheduleReviewTeamMemberType = NonNullable<
  ScheduleReviewQueryCgResponse['scheduleReview']
>['team'][number];

// One "Needs Attention" card; severity/scope/action are GraphQL enums, so the
// FE keys off the generated string-literal unions rather than free strings.
// `needsAttention` is nullable server-side (null = analyzer errored, panel hides;
// [] = nothing to flag, panel affirms), so unwrap it before indexing the element.
export type ScheduleReviewNeedsAttentionType = NonNullable<
  NonNullable<
    ScheduleReviewQueryCgResponse['scheduleReview']
  >['weeks'][number]['needsAttention']
>[number];

// Read-only schedule-review grid for a (balancer run, organization unit):
// the session header, shift-type bands, role pills, the full Sun–Sat weeks
// (day columns, placed shifts, per-week status and pendingCount), and the
// active home-unit roster (Team tab + worker profile panel).
export const SCHEDULE_REVIEW_QUERY: TypedDocumentNode<
  ScheduleReviewQueryCgResponse,
  ScheduleReviewQueryVariables
> = gql`
  query ScheduleReview($balancerRunId: ID!, $organizationUnitId: ID!) {
    scheduleReview(
      balancerRunId: $balancerRunId
      organizationUnitId: $organizationUnitId
    ) {
      awaitingReview
      chargeRole
      unitDecision
      unitName
      schedulePeriodId
      serviceLineName
      serviceLineUnits {
        id
        name
      }
      aiSummary {
        whatBalancerDid
        callouts
        generatedAt
      }
      roles
      eligibleRolePairs
      shiftTypeSections {
        key
        label
        time
        icon
      }
      weeks {
        key
        label
        status
        pendingCount
        hasCoverageGap
        needsAttention {
          key
          severity
          title
          detail
          action
          scope
          targetShiftIds
          targetWorkerUuids
        }
        days {
          key
          date
          dayLabel
          dateLabel
          weekend
        }
        shifts {
          id
          dayKey
          shiftTypeSection
          role
          isChargeRole
          startHour
          startTime
          endTime
          workerName
          assignedWorkerId
          source
          suggested
          fixedCommitment
          assignedReasons {
            key
            label
            hard
            weight
          }
        }
        demands {
          dayKey
          role
          shiftTypeSection
          plannedCount
        }
      }
      team {
        id
        name
        unit
        role
        employmentType
        shiftType
        shiftPattern
        chargeEligible
        terms {
          effectiveStartDate
          effectiveEndDate
          role
          chargeEligible
          active
          homeUnitMember
        }
        active
        homeUnitMember
        fteHours
        scheduledHours
        remainingHours
        weekendTarget
        weekendWillingness
        weekendCount
      }
    }
  }
`;
